import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/synapse/Icon';
import { cn } from '@/lib/utils';

interface SummaryCardProps {
  titleKey: string;
  icon?: IconName;
  danger?: boolean;
  action?: ReactNode;
  children: ReactNode;
}

/**
 * Generic titled card used across the patient summary: rounded-lg, hairline
 * border, header row with optional leading icon and trailing action. The
 * `danger` variant paints the border + title red (used for the Allergies card).
 */
export function SummaryCard({ titleKey, icon, danger, action, children }: SummaryCardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border bg-paper',
        danger ? 'border-destructive' : 'border-line',
      )}
    >
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        {icon && (
          <Icon name={icon} size={15} className={danger ? 'text-destructive' : 'text-muted'} />
        )}
        <span
          className={cn(
            'text-[13px] font-bold tracking-[-0.01em]',
            danger ? 'text-destructive' : 'text-ink',
          )}
        >
          {t(titleKey)}
        </span>
        {action && <span className="ml-auto text-[12px] text-muted">{action}</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** A bordered list row inside a SummaryCard. The first row has no top border. */
export function SummaryCardRow({ children, first }: { children: ReactNode; first?: boolean }) {
  return <div className={cn('px-4 py-3', !first && 'border-t border-line')}>{children}</div>;
}
